// frontend/src/App.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import Modal from 'react-modal';
import { PermissionsProvider, usePermissions } from './PermissionsContext';
import axios from 'axios'; // Import axios
import './App.css';
import LoginPage from './LoginPage';
import AppExplorer from './AppExplorer';
import SettingsPage from './SettingsPage';
import DataManagementPage from './DataManagementPage';
import ChatWindow from './ChatWindow';
import HomePage from './HomePage'; // <-- Import HomePage
import PolicyBot from './PolicyBot';
import CPSpecialistFormatterPage from './cp_formatter_module/formatter_page';
import Setup2FA from './Setup2FA';
import VerifyOTP from './VerifyOTP';
import PipelineRunnerPage from './PipelineRunnerPage';
import ProtectedElement from './ProtectedElement';
import ProtectedRoute from './ProtectedRoute';
import { logEvent } from './utils/logEvent';

Modal.setAppElement('#root');

// --- Default Settings and Provider/Model Map (moved to App.js context) ---
const defaultSettings = {
    llmProvider: 'OpenAI',
    model: 'gpt-4o-mini',
    temperature: 0.5,
    maxTokens: 2500,
    url: 'https://api.openai.com/v1/chat/completions', // Default URL
};

const providerModelMap = {
     OpenAI: [
        // model_max_tokens here is used to set the model max output tokens
         { name: 'gpt-4o-mini', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '16000' },
         { name: 'gpt-4o', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '128000'  },
         { name: 'gpt-5-mini', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '16000'  },
         { name: 'gpt-5', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '16000'  },
         { name: 'gpt-4.1', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '16000'  },
         { name: 'gpt-4.1-mini', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '16000'  },
         { name: 'gpt-4.1-nano', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '16000'  },
         { name: 'gpt-5-nano', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '16000'  },
         { name: 'o3-mini', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '128000'  },
         { name: 'o4-mini', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '128000'  },
         { name: 'o1-mini', url: 'https://api.openai.com/v1/chat/completions', model_max_tokens: '128000'  },
     ],
     Anthropic: [
         { name: 'claude-3-5-sonnet-latest', url: 'https://api.anthropic.com/v1/messages', model_max_tokens: '8192'  },
         { name: 'claude-3-opus-latest', url: 'https://api.anthropic.com/v1/messages', model_max_tokens: '4096'  },
         { name: 'claude-3-sonnet-20240229', url: 'https://api.anthropic.com/v1/messages', model_max_tokens: '4096'  },
         { name: 'claude-3-haiku-20240307', url: 'https://api.anthropic.com/v1/messages', model_max_tokens: '4096'  },
     ],
     OpenSource: [
         { name: 'meta-llama/llama-4-scout-17b-16e-instruct', url: '', model_max_tokens: '131072'  },
         { name: 'deepseek-r1-distill-llama-70b', url: '', model_max_tokens: '128000'  },
         { name: 'llama-3.1-8b-instant', url: '', model_max_tokens: '128000'  },
         { name: 'meta-llama/llama-4-maverick-17b-128e-instruct', url: '', model_max_tokens: '131072'  },
     ],
     Grok: [
        { name: 'grok-3-mini', url: '', model_max_tokens: '131072'  },
        { name: 'grok-4', url: '', model_max_tokens: '131072'  },
        { name: 'grok-3', url: '', model_max_tokens: '131072'  },
    ],
     Local: [
         { name: 'llama3:latest', url: 'http://localhost:11434/api/chat', model_max_tokens: '8192' },
     ]
 };


function AppWithRouter() {
    // --- Authentication State ---
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authUsername, setAuthUsername] = useState('');
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [postAuthInitializing, setPostAuthInitializing] = useState(false); // show tiny spinner while applying theme
    // --- Tab Session Key (used to differentiate refresh vs. closed tab) ---
    const TAB_SESSION_KEY = 'tab_session';
    // --- Model List State (kept for compatibility but primary source is computed) ---
    const [availableModels, setAvailableModels] = useState(providerModelMap[defaultSettings.llmProvider] || []);
    const [theme, setTheme] = useState('LIGHT'); // Default to LIGHT theme
    // --- Router Helpers ---
    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname || '/';
    const currentView = useMemo(() => {
        switch (pathname) {
            case '/': return 'home';
            case '/explorer': return 'explorer';
            case '/config': return 'config';
            case '/data': return 'data';
            case '/chat': return 'chat';
            case '/pipeline': return 'pipeline';
            case '/policy-bot': return 'policyBot';
            case '/cp-formatter': return 'cpFormatter';
            default: return 'home';
        }
    }, [pathname]);

    // --- Shared State ---
    const [directories, setDirectories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [userLLMSettings, setUserLLMSettings] = useState(null);

    // --- Backend Busy Tracking (prevents inactivity logout during work) ---
    const [inflightRequestCount, setInflightRequestCount] = useState(0);
    const [isChatBackendBusy, setIsChatBackendBusy] = useState(false);
    const isBackendBusy = inflightRequestCount > 0 || isChatBackendBusy;
    const isBackendBusyRef = useRef(isBackendBusy);
    const idleTimerClearRef = useRef(null);
    const idleTimerResetRef = useRef(null);
    useEffect(() => {
        isBackendBusyRef.current = isBackendBusy;
    }, [isBackendBusy]);

    // Track axios in-flight requests globally (covers most backend calls).
    useEffect(() => {
        const requestInterceptor = axios.interceptors.request.use(
            (config) => {
                // Mark and count each request once.
                config.__pearlInFlight = true;
                setInflightRequestCount((c) => c + 1);
                return config;
            },
            (err) => Promise.reject(err)
        );

        const responseInterceptor = axios.interceptors.response.use(
            (response) => {
                const cfg = response?.config;
                if (cfg?.__pearlInFlight) {
                    cfg.__pearlInFlight = false;
                    setInflightRequestCount((c) => Math.max(0, c - 1));
                }
                return response;
            },
            (error) => {
                const cfg = error?.config;
                if (cfg?.__pearlInFlight) {
                    cfg.__pearlInFlight = false;
                    setInflightRequestCount((c) => Math.max(0, c - 1));
                }
                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.request.eject(requestInterceptor);
            axios.interceptors.response.eject(responseInterceptor);
        };
    }, []);

    // Helper to normalize backend settings shape to camelCase used in UI
    const normalizeUserLLMSettings = (raw) => {
        if (!raw) return null;
        // Resolve provider case-insensitively to known providerModelMap keys
        const rawProvider = (raw.llmprovider || raw.llmProvider || defaultSettings.llmProvider || '').toString();
        const providerKey = Object.keys(providerModelMap).find(k => k.toLowerCase() === rawProvider.toLowerCase()) || defaultSettings.llmProvider;

        const normalized = {
            ...raw,
            llmProvider: providerKey,
            model: raw.model || defaultSettings.model,
            temperature: typeof raw.temperature === 'number' ? raw.temperature : parseFloat(raw.temperature) || defaultSettings.temperature,
            maxTokens: raw.max_tokens || raw.maxTokens || defaultSettings.maxTokens,
            chat_settings: raw.chat_settings || raw.chatSettings || {},
        };
        // Resolve url from providerModelMap if possible
    const providerModels = providerModelMap[normalized.llmProvider];
        const modelDetails = providerModels?.find(m => m.name === normalized.model);
        normalized.url = modelDetails?.url || raw.url || defaultSettings.url;
        return normalized;
    };

    // --- Header LLM Settings Editing State ---
    const [isEditingHeaderSettings, setIsEditingHeaderSettings] = useState(false);
    const [editableHeaderSettings, setEditableHeaderSettings] = useState(null);
    const [headerMaxTokenValue, setHeaderMaxTokenValue] = useState(defaultSettings.maxTokens);
    const [isSavingHeaderSettings, setIsSavingHeaderSettings] = useState(false);


    // --- Explorer Specific Config State (Managed Here) ---
    const [userRole, setUserRole] = useState('');
    const [initialQuery, setInitialQuery] = useState('');
    const [maxLoops, setMaxLoops] = useState(5);
    const [analyzeResponse, setAnalyzeResponse] = useState(false);
    const [verifyCheckin, setVerifyCheckin] = useState(1);
    const [useRAG, setUseRAG] = useState(false);

    // --- ADDED: Ref to access ChatWindow's imperative methods ---
    const chatWindowRef = useRef(null);

    // --- MODIFIED: `handleLogout` to check for unsaved chat ---
    const { refreshPermissions, clearPermissions } = usePermissions();

    const handleLogout = useCallback(async (clearAll = true, force = false) => {
        // Before doing anything, check if we're leaving an unsaved chat.
        if (!force && currentView === 'chat' && chatWindowRef.current) {
            const canProceed = await chatWindowRef.current.handleBeforeNavigate();
            if (!canProceed) {
                setIsLoading(false); // Ensure loading state is correct if user cancels.
                return; // User chose not to proceed with logout.
            }
        }

        console.log("handleLogout called.");
        // Fire-and-forget logout event
        logEvent('logout', { clearAll, force, view: currentView }).catch(() => {});
        setIsLoading(true); setError(null);
        try {
            await axios.post('/api/logout', {}, { withCredentials: true });
        } catch (err) { console.error("Error during backend logout request:", err); }
         finally {
            setIsAuthenticated(false);
            setAuthUsername('');
            setDirectories([]);
            navigate('/');
            setInitialQuery(''); setUserRole('');
            setMaxLoops(5); setAnalyzeResponse(false); setVerifyCheckin(1); setUseRAG(false);
            setUserLLMSettings(null);
            setIsEditingHeaderSettings(false);
            setEditableHeaderSettings(null);
            setIsSavingHeaderSettings(false);
            setIsLoading(false);
            setTheme('LIGHT');
            console.log("User logged out.");
            // Clear permissions after logout
            clearPermissions();
            // Clear tab session marker on logout
            try { sessionStorage.removeItem(TAB_SESSION_KEY); } catch (e) { /* ignore */ }
        }
    }, [currentView, navigate, clearPermissions]);

    const fetchUserLLMSettings = useCallback(async (calledFrom = "unknown") => {
        if (!isAuthenticated) return;
        console.log(`App.js: Fetching user LLM settings (called from ${calledFrom})...`);
        try {
            const settingsResponse = await axios.get('/api/user/settings', { withCredentials: true });
            const fetchedSettings = settingsResponse.data;
            if (fetchedSettings) {
                // Parsing logic from original fetchInitialAppData
                const normalized = normalizeUserLLMSettings(fetchedSettings);
                setUserLLMSettings(normalized);
                console.log("App.js: User LLM Settings set/refreshed:", normalized);
            } else {
                setUserLLMSettings(null); // Or set to defaults if backend returns nothing
                console.log("App.js: No settings data received from backend, userLLMSettings set to null.");
            }
        } catch (e) {
            console.error("App.js: Error fetching/parsing user settings:", e);
            if (e.response?.status === 401) {
                 handleLogout(false); setError('Session expired. Please log in.');
            } else if (e.response?.status === 404) {
                console.warn("App.js: User LLM settings not found on backend. Using defaults.");
                setUserLLMSettings({ ...defaultSettings, chat_settings: {} }); // Fallback to defaults
            } else {
                setError(`Failed to fetch user settings: ${e.message}.`);
            }
        }
    }, [isAuthenticated, handleLogout]); // Added handleLogout to dependencies

    const fetchInitialAppData = useCallback(async () => {
        if (!isAuthenticated) return;
        console.log("App.js: Fetching initial app data...");
        setIsLoading(true); setError(null);
        try {
            const dirResponse = await axios.get('/api/directories', { withCredentials: true });
            setDirectories(dirResponse.data.directories || []);
            await fetchUserLLMSettings("fetchInitialAppData"); // Use the new specific function
        } catch (e) {
            console.error("App.js: Error fetching initial app data:", e);
            // Error handling for dir/convo fetch is still relevant here
            if (e.response?.status === 401 && e.config.url !== '/api/user/settings') { // Avoid double logout if settings fetch failed
                 handleLogout(false); setError('Session expired. Please log in.');
            } else if (e.config.url !== '/api/user/settings') {
                 setError(`Failed to fetch initial app data: ${e.message}.`);
            }
        } finally { setIsLoading(false); }
    }, [isAuthenticated, fetchUserLLMSettings, handleLogout]); // Added handleLogout

    // availableModelsForHeader is derived from current provider (editable or user settings)
    const availableModelsForHeader = useMemo(() => {
        const provider = isEditingHeaderSettings && editableHeaderSettings?.llmProvider
            ? editableHeaderSettings.llmProvider
            : userLLMSettings?.llmProvider || defaultSettings.llmProvider;
        return providerModelMap[provider] || [];
    }, [isEditingHeaderSettings, editableHeaderSettings?.llmProvider, userLLMSettings?.llmProvider]);

    useEffect(() => {
        const checkSession = async () => {
            setIsInitialLoading(true);
            try {
                const response = await axios.get('/api/check_session', { withCredentials: true });
                const tabHasSession = (() => { try { return !!sessionStorage.getItem(TAB_SESSION_KEY); } catch { return false; } })();
                if (response.data.isAuthenticated && tabHasSession) {
                    setIsAuthenticated(true);
                    setAuthUsername(response.data.username);
                    if (response.data.theme) {
                        setTheme(response.data.theme);
                    }
                } else {
                    // Either not authenticated with server, or this is a new tab (no tab session)
                    setIsAuthenticated(false);
                    setAuthUsername('');
                    // keep default theme when unauthenticated; don't force dark
                    setTheme('LIGHT');
                }
            } catch (err) {
                setIsAuthenticated(false);
                setAuthUsername('');
                // network error or server issues: keep default theme
                setTheme('LIGHT');
            }
            finally { setIsInitialLoading(false); }
        };
        checkSession();
    }, []);
    
    useEffect(() => {
        document.body.className = '';
        document.body.classList.add(`theme-${theme.toLowerCase()}`);
        console.log(`Theme set to: ${theme}`);
    }, [theme]); 

    useEffect(() => {
        if (isAuthenticated && !isInitialLoading) {
            // After a successful auth, refresh session info (username/theme) from server
            const refreshSession = async () => {
                setPostAuthInitializing(true);
                try {
                    const resp = await axios.get('/api/check_session', { withCredentials: true });
                    if (resp?.data?.isAuthenticated) {
                        if (resp.data.username) setAuthUsername(resp.data.username);
                        if (resp.data.theme) setTheme(resp.data.theme);
                    }
                } catch (e) {
                    console.warn('Failed to refresh session after login:', e);
                } finally {
                    setPostAuthInitializing(false);
                }
            };
            refreshSession(); // keep the UI gated until theme applied
            fetchInitialAppData(); // can proceed in parallel; theme gate is separate
            // Fetch permissions when user becomes authenticated
            refreshPermissions();
        }
        if (!isAuthenticated && !isInitialLoading) {
            setUserLLMSettings(null);
            setIsEditingHeaderSettings(false);
            setEditableHeaderSettings(null);
            clearPermissions();
        }
    }, [isAuthenticated, isInitialLoading, fetchInitialAppData, refreshPermissions, clearPermissions]);

    useEffect(() => {
        if (isEditingHeaderSettings) {
            if (userLLMSettings) {
                setEditableHeaderSettings({ ...userLLMSettings });
            } else {
                // If settings not loaded, don't open edit mode yet
                setIsEditingHeaderSettings(false);
                setEditableHeaderSettings(null);
            }
        } else {
            setEditableHeaderSettings(null);
        }
    }, [isEditingHeaderSettings, userLLMSettings]);

    useEffect(() => {
        const settingsSource = isEditingHeaderSettings && editableHeaderSettings ? editableHeaderSettings : userLLMSettings;
        if (settingsSource && settingsSource.llmProvider && settingsSource.model) {
            const providerModels = providerModelMap[settingsSource.llmProvider];
            const currentModelDetails = providerModels?.find(m => m.name === settingsSource.model);
            const newMax = currentModelDetails ? parseInt(currentModelDetails.model_max_tokens, 10) : defaultSettings.maxTokens;
            setHeaderMaxTokenValue(isNaN(newMax) || newMax <=0 ? defaultSettings.maxTokens : newMax);
        } else {
            setHeaderMaxTokenValue(defaultSettings.maxTokens);
        }
    }, [editableHeaderSettings, userLLMSettings, isEditingHeaderSettings]);

    useEffect(() => {
        document.body.className = '';
        document.body.classList.add(`theme-${theme.toLowerCase()}`);
    }, [theme]);

    const handleSettingsSavedInChatWindow = useCallback(() => {
        console.log("App.js: Settings saved in ChatWindow, triggering refresh of userLLMSettings.");
        fetchUserLLMSettings("handleSettingsSavedInChatWindow");
    }, [fetchUserLLMSettings]);

    const handleEditableHeaderChange = useCallback((key, value) => {
        setEditableHeaderSettings(prev => {
            let newSettings = { ...(prev || defaultSettings) };

            if (key === 'llmProvider') {
                const firstModelOfNewProvider = providerModelMap[value]?.[0];
                if (firstModelOfNewProvider) {
                    const modelMax = parseInt(firstModelOfNewProvider.model_max_tokens, 10) || defaultSettings.maxTokens;
                    newSettings = {
                        ...newSettings,
                        llmProvider: value,
                        model: firstModelOfNewProvider.name,
                        url: firstModelOfNewProvider.url,
                        maxTokens: Math.min(newSettings.maxTokens || defaultSettings.maxTokens, modelMax)
                    };
                } else {
                    newSettings = { ...newSettings, llmProvider: value, model: '', url: '', maxTokens: defaultSettings.maxTokens };
                }
            } else if (key === 'model') {
                const providerModels = providerModelMap[newSettings.llmProvider];
                const selectedModelDetails = providerModels?.find(m => m.name === value);
                if (selectedModelDetails) {
                    const modelMax = parseInt(selectedModelDetails.model_max_tokens, 10) || defaultSettings.maxTokens;
                    newSettings = {
                        ...newSettings,
                        model: value,
                        url: selectedModelDetails.url,
                        maxTokens: Math.min(newSettings.maxTokens || defaultSettings.maxTokens, modelMax)
                    };
                } else {
                    newSettings = { ...newSettings, model: value };
                }
            } else if (key === 'temperature') {
                const numericValue = parseFloat(value);
                if (!isNaN(numericValue)) {
                    newSettings = { ...newSettings, temperature: Math.max(0, Math.min(2, numericValue)) };
                }
            } else if (key === 'maxTokens') {
                const numericValue = parseInt(value, 10);
                 if (!isNaN(numericValue)) {
                    const currentProviderModels = providerModelMap[newSettings.llmProvider];
                    const currentModelInfo = currentProviderModels?.find(m => m.name === newSettings.model);
                    const modelMaxLimit = currentModelInfo ? parseInt(currentModelInfo.model_max_tokens, 10) : headerMaxTokenValue;
                    const cappedValue = Math.min(numericValue, modelMaxLimit || 1);
                    newSettings = { ...newSettings, maxTokens: Math.max(1, cappedValue) };
                 }
            }
            return newSettings;
        });
    }, [headerMaxTokenValue]);

    const toggleHeaderSettingsEdit = () => {
        if (isEditingHeaderSettings) {
            // Turning off edit mode
            setEditableHeaderSettings(null);
            setIsEditingHeaderSettings(false);
            setError(null);
            return;
        }

        // Turning on edit mode: initialize editable state immediately from user's settings
        if (userLLMSettings) {
            const provider = userLLMSettings.llmProvider || defaultSettings.llmProvider;
            const modelsForProvider = providerModelMap[provider] || [];

            // Ensure model is valid for provider, otherwise pick first available
            const validModel = userLLMSettings.model && modelsForProvider.find(m => m.name === userLLMSettings.model)
                ? userLLMSettings.model
                : (modelsForProvider[0]?.name || '');

            setEditableHeaderSettings({ ...userLLMSettings, model: validModel, llmProvider: provider });
            setIsEditingHeaderSettings(true);
        } else {
            // Settings not ready yet; keep edit mode off and show error
            setError('Cannot edit LLM settings yet: user settings not loaded.');
            setIsEditingHeaderSettings(false);
            setEditableHeaderSettings(null);
        }
    };

    const saveHeaderSettings = async () => {
        if (!editableHeaderSettings) return;
        setIsSavingHeaderSettings(true); setError(null);
        try {
            const payload = {
                llmProvider: editableHeaderSettings.llmProvider,
                model: editableHeaderSettings.model,
                temperature: editableHeaderSettings.temperature.toString(),
                maxTokens: editableHeaderSettings.maxTokens.toString(),
            };
            await axios.post('/api/user/settings', payload, { withCredentials: true });

            const updatedSettingsResponse = await axios.get('/api/user/settings', { withCredentials: true });
            const fetchedSettings = updatedSettingsResponse.data;
                if (fetchedSettings) {
                    const normalized = normalizeUserLLMSettings(fetchedSettings);
                    setUserLLMSettings(normalized);
                    console.log("Saved and Refreshed User LLM Settings:", normalized);
                } else {
                    console.warn("Failed to re-fetch settings after save, using local editable state.");
                    setUserLLMSettings({ ...editableHeaderSettings });
                }

            setIsEditingHeaderSettings(false);
        } catch (err) {
            console.error("Error saving header settings:", err);
            const errorMsg = err.response?.data?.error || err.message || "Failed to save settings.";
            setError(`Save failed: ${errorMsg}`);
        } finally {
            setIsSavingHeaderSettings(false);
        }
    };

     const updateUserLLMSettings = (newSettings) => {
        console.log("App.js: Updating userLLMSettings from ConfigSection:", newSettings);
        const consistentSettings = {
            ...newSettings,
            llmProvider: newSettings.llmprovider || newSettings.llmProvider,
            maxTokens: newSettings.max_tokens || newSettings.maxTokens,
            chat_settings: newSettings.chat_settings || userLLMSettings?.chat_settings || {},
        };
        delete consistentSettings.llmprovider; 
        delete consistentSettings.max_tokens; 

        setUserLLMSettings(consistentSettings);
    };

    const handleSaveConfig = useCallback((newConfig) => {
        setMaxLoops(newConfig.maxLoops);
        setAnalyzeResponse(newConfig.analyzeResponse);
        setVerifyCheckin(newConfig.verifyCheckin);
        setUseRAG(newConfig.useRAG);
    }, []);

    // --- ADDED: Navigation Guard Function ---
    const handleBeforeNavigate = async () => {
        if (currentView === 'chat' && chatWindowRef.current) {
            const canProceed = await chatWindowRef.current.handleBeforeNavigate();
            if (!canProceed) {
                return false; // Navigation blocked by user or save failure
            }
        }
        return true; // Navigation is allowed
    };

    // --- MODIFIED: Navigation functions to be async and use the guard ---
    const navigateToHome = async () => {
        if (!(await handleBeforeNavigate())) return;
        navigate('/');
        setIsEditingHeaderSettings(false);
    };
    const navigateToExplorer = async () => {
        if (!(await handleBeforeNavigate())) return;
        navigate('/explorer');
        setIsEditingHeaderSettings(false);
    };
    const navigateToConfig = async () => {
        if (!(await handleBeforeNavigate())) return;
        navigate('/config');
        setIsEditingHeaderSettings(false);
    };
    const navigateToDataManagement = async () => {
        if (!(await handleBeforeNavigate())) return;
        navigate('/data');
        setIsEditingHeaderSettings(false);
    };
    const navigateToChat = async () => {
        if (!(await handleBeforeNavigate())) return;
        navigate('/chat');
        setIsEditingHeaderSettings(false);
    };

    const navigateToPolicyBot = async () => {
        if (!(await handleBeforeNavigate())) return;
        navigate('/policy-bot');
        setIsEditingHeaderSettings(false);
    };

    const navigateToPipeline = async () => {
        if (!(await handleBeforeNavigate())) return;
        navigate('/pipeline');
        setIsEditingHeaderSettings(false);
    };

    const navigateToCPSpecialistFormatter = async () => {
        if (!(await handleBeforeNavigate())) return;
        navigate('/cp-formatter');
        setIsEditingHeaderSettings(false);
    }

    // If authenticated and on login/2FA pages, redirect to home
    useEffect(() => {
        if (isAuthenticated && (pathname === '/login' || pathname === '/setup-2fa' || pathname === '/verify-otp')) {
            navigate('/');
        }
    }, [isAuthenticated, pathname, navigate]);

    // --- Inactivity Auto-Logout (12 minutes) ---
    useEffect(() => {
        if (!isAuthenticated) return;

        const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
        let idleTimer = null;

        const clearTimer = () => {
            if (idleTimer) {
                clearTimeout(idleTimer);
                idleTimer = null;
            }
        };

        const resetTimer = () => {
            // While backend work is running, we do NOT start the countdown.
            if (isBackendBusyRef.current) {
                clearTimer();
                return;
            }
            clearTimer();
            idleTimer = setTimeout(async () => {
                // If backend work started since the timer was scheduled, defer logout.
                if (isBackendBusyRef.current) {
                    clearTimer();
                    return;
                }

                console.log('Auto-logout due to inactivity');
                // Log specific reason for analytics clarity
                logEvent('logout', { reason: 'inactivity', auto: true, view: currentView }).catch(() => {});

                // If an unsaved chat is open, save it before logging out.
                if (currentView === 'chat' && chatWindowRef.current?.saveUnsavedChatSilently) {
                    const saved = await chatWindowRef.current.saveUnsavedChatSilently();
                    if (!saved) {
                        setError('Auto-logout paused: could not save your current chat. Please return to the tab to resolve.');
                        // Keep the user signed in; restart idle timer.
                        resetTimer();
                        return;
                    }
                }

                // Force logout without unsaved chat prompt (chat already saved above).
                handleLogout(true, true);
            }, IDLE_TIMEOUT_MS);
        };

        // Expose timer controls so a separate effect can pause/resume on busy transitions.
        idleTimerClearRef.current = clearTimer;
        idleTimerResetRef.current = resetTimer;

        const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
        activityEvents.forEach(evt => window.addEventListener(evt, resetTimer, { passive: true }));
        resetTimer();

        return () => {
            clearTimer();
            activityEvents.forEach(evt => window.removeEventListener(evt, resetTimer));
            idleTimerClearRef.current = null;
            idleTimerResetRef.current = null;
        };
    }, [isAuthenticated, handleLogout, currentView]);

    // Pause the inactivity countdown while backend work is running; restart when it finishes.
    useEffect(() => {
        if (!isAuthenticated) return;
        if (isBackendBusy) {
            idleTimerClearRef.current?.();
        } else {
            idleTimerResetRef.current?.();
        }
    }, [isBackendBusy, isAuthenticated]);

    // Log page views whenever the route changes (including unauth routes)
    useEffect(() => {
        const page = {
            name: currentView,
            pathname,
        };
        logEvent('page_view', page).catch(() => {});
    }, [pathname, currentView]);

    if (isInitialLoading) { return <div className="loading-fullpage">Checking session...</div>; }
    if (postAuthInitializing) { return <div className="loading-fullpage">Applying preferences…</div>; }

    const getHeaderTitle = () => {
        switch(currentView) {
            case 'home': return 'Home'; case 'explorer': return 'Explorer';
            case 'config': return 'Config'; case 'data': return 'Data Management'; case 'chat': return 'Chat';
            case 'pipeline': return 'Pipeline Runner';
            default: return 'App';
        }
    };

    // availableModelsForHeader is computed above via useMemo
    const tempPercentForHeader = editableHeaderSettings ? ((editableHeaderSettings.temperature || 0) / 2) * 100 : 50;
    const tokenPercentForHeader = editableHeaderSettings && headerMaxTokenValue > 1 ? (((editableHeaderSettings.maxTokens || 1) - 1) / (headerMaxTokenValue - 1)) * 100 : 0;


    // Unauthenticated view: show only Login and 2FA routes, no header
    if (!isAuthenticated) {
        return (
            <div className="App">
                {error && (
                    <div className="error app-global-error" onClick={() => setError(null)} title="Click to dismiss error">{error}</div>
                )}
                <Routes>
                    <Route path="/login" element={<LoginPage setIsAuthenticated={setIsAuthenticated} setAuthUsername={setAuthUsername} />} />
                    <Route path="/setup-2fa" element={<Setup2FA setIsAuthenticated={setIsAuthenticated} setAuthUsername={setAuthUsername} />} />
                    <Route path="/verify-otp" element={<VerifyOTP setIsAuthenticated={setIsAuthenticated} setAuthUsername={setAuthUsername} />} />
                    <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
            </div>
        );
    }

    return (
        <div className="App">
            <header className="app-header">
                 <h1>
                    <img
                        src={`${process.env.PUBLIC_URL}/Pearlify-logo.png`}
                        alt="Pearlify Logo"
                        className="header-logo"
                    />
                    Pearlify {getHeaderTitle()}
                 </h1>

                 {currentView === 'chat' && (
                    isEditingHeaderSettings && editableHeaderSettings ? (
                        <div className="header-llm-settings-edit">
                            <div className="header-setting-item">
                                <label htmlFor="header-llmProvider">Provider</label>
                                <select id="header-llmProvider" value={editableHeaderSettings.llmProvider} onChange={(e) => handleEditableHeaderChange('llmProvider', e.target.value)} disabled={isSavingHeaderSettings}>
                                    {Object.keys(providerModelMap).map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </div>
                            <div className="header-setting-item">
                                <label htmlFor="header-llmModel">Model</label>
                                <select id="header-llmModel" value={editableHeaderSettings.model} onChange={(e) => handleEditableHeaderChange('model', e.target.value)} disabled={isSavingHeaderSettings || !editableHeaderSettings.llmProvider || availableModelsForHeader.length === 0}>
                                    {availableModelsForHeader.length > 0 ?
                                        availableModelsForHeader.map(m => <option key={m.name} value={m.name}>{m.name}</option>) :
                                        <option value="">N/A</option>}
                                </select>
                            </div>
                            <div className="header-setting-item slider">
                                <label htmlFor="header-temp">Temp: <span>{editableHeaderSettings.temperature?.toFixed(2)}</span></label>
                                <input type="range" id="header-temp" min="0" max="2" step="0.05" value={editableHeaderSettings.temperature} onChange={(e) => handleEditableHeaderChange('temperature', parseFloat(e.target.value))} disabled={isSavingHeaderSettings} style={{ '--value-percent': `${tempPercentForHeader}%` }}/>
                            </div>
                            <div className="header-setting-item slider">
                                <label htmlFor="header-tokens">MaxT: <span>{editableHeaderSettings.maxTokens?.toLocaleString()}</span> <small>(Limit: {headerMaxTokenValue?.toLocaleString()})</small></label>
                                <input type="range" id="header-tokens" min="1" max={headerMaxTokenValue} step={headerMaxTokenValue > 1000 ? 50 : 1} value={editableHeaderSettings.maxTokens} onChange={(e) => handleEditableHeaderChange('maxTokens', parseInt(e.target.value,10))} disabled={isSavingHeaderSettings || !headerMaxTokenValue || headerMaxTokenValue <= 1} style={{ '--value-percent': `${tokenPercentForHeader}%` }}/>
                            </div>
                            <div className="header-setting-actions">
                                <button onClick={saveHeaderSettings} disabled={isSavingHeaderSettings} className="header-save-button">
                                    {isSavingHeaderSettings ? 'Saving...' : 'Save'}
                                </button>
                                <button onClick={toggleHeaderSettingsEdit} disabled={isSavingHeaderSettings} className="header-cancel-button">Cancel</button>
                            </div>
                        </div>
                    ) : userLLMSettings ? (
                        <div className="header-llm-settings" title={`Provider: ${userLLMSettings.llmProvider}\nModel: ${userLLMSettings.model}\nTemp: ${userLLMSettings.temperature}\nMax Tokens: ${userLLMSettings.maxTokens || userLLMSettings.max_tokens}`}>
                            <span className="setting-item">
                                <span className="setting-label">Provider:</span>
                                <span className="setting-value">{userLLMSettings.llmProvider}</span>
                            </span>
                            <span className="setting-item">
                                <span className="setting-label">Model:</span>
                                <span className="setting-value model">{userLLMSettings.model}</span>
                            </span>
                            <span className="setting-item">
                                <span className="setting-label">Temp:</span>
                                <span className="setting-value">
                                    {typeof userLLMSettings.temperature === 'number' ? userLLMSettings.temperature.toFixed(1) : 'N/A'}
                                </span>
                            </span>
                            <span className="setting-item">
                                <span className="setting-label">MaxT:</span>
                                <span className="setting-value">
                                    {typeof (userLLMSettings.maxTokens || userLLMSettings.max_tokens) === 'number' ? (userLLMSettings.maxTokens || userLLMSettings.max_tokens).toLocaleString() : 'N/A'}
                                </span>
                            </span>
                            <ProtectedElement required={"user:update-settings"} behavior='disable' disabledTooltip='You do not have permission to update LLM settings.'>
                            <button onClick={toggleHeaderSettingsEdit} className="header-edit-button" title="Edit LLM Settings">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16"><path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/><path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"/></svg>
                            </button>
                            </ProtectedElement>
                        </div>
                    ) : (
                        <div className="header-llm-settings-empty">
                            <span>LLM settings not set.</span>
                            <button onClick={toggleHeaderSettingsEdit} className="header-edit-button" title="Configure LLM Settings">Configure</button>
                        </div>
                    )
                 )}

                 <div className="header-controls">
                     <span className="welcome-user">Welcome, {authUsername || 'User'}!</span>
                    
                    {currentView !== 'home' && (
                        <button onClick={navigateToHome} className="nav-button" disabled={isLoading}>Home</button>
                    )}

                    {currentView === 'home' ? (
                        // On Home page, show buttons to go to other main sections
                        <>
                            {/* <ProtectedElement required="page:kn-explorer-access" behavior='hide'>
                                <button onClick={navigateToExplorer} className="nav-button" disabled={isLoading}>Explorer</button>
                            </ProtectedElement> */}
                            <button onClick={navigateToChat} className="nav-button" disabled={isLoading}>Chat</button>
                            <button onClick={navigateToConfig} className="nav-button" disabled={isLoading}>Config</button>
                            {/* <button onClick={navigateToDataManagement} className="nav-button" disabled={isLoading}>Data</button> */}
                            {/* <ProtectedElement required="page:pipeline-access" behavior='hide'>
                                <button onClick={navigateToPipeline} className="nav-button" disabled={isLoading}>Pipeline</button>
                            </ProtectedElement> */}
                            {/* <ProtectedElement required="page:policy-bot-access" behavior='hide'>
                                <button onClick={navigateToPolicyBot} className="nav-button" disabled={isLoading}>Policy Bot</button>
                            </ProtectedElement> */}
                        </>
                    ) : (
                        // On other pages (Explorer, Chat, Config), show buttons to other main sections
                        <>
                            {/* {currentView !== 'explorer' && (
                                <ProtectedElement required="page:kn-explorer-access" behavior='hide'>
                                    <button onClick={navigateToExplorer} className="nav-button" disabled={isLoading}>Explorer</button>
                                </ProtectedElement>
                            )} */}
                            {currentView !== 'chat' && (
                                <button onClick={navigateToChat} className="nav-button" disabled={isLoading}>Chat</button>
                            )}
                            {currentView !== 'config' && (
                                <button onClick={navigateToConfig} className="nav-button" disabled={isLoading}>Config</button>
                            )}
                            {/* {currentView !== 'data' && (
                                <button onClick={navigateToDataManagement} className="nav-button" disabled={isLoading}>Data</button>
                            )}
                            {currentView !== 'pipeline' && (
                                <ProtectedElement required="page:pipeline-access" behavior='hide'>
                                    <button onClick={navigateToPipeline} className="nav-button" disabled={isLoading}>Pipeline</button>
                                </ProtectedElement>
                            )} */}
                        </>
                    )}
                     <button onClick={() => handleLogout(true)} className="logout-button" disabled={isLoading}> Logout </button>
                 </div>
            </header>

            {error && <div className="error app-global-error" onClick={() => setError(null)} title="Click to dismiss error">{error}</div>}

            <Routes>
                <Route
                    path="/"
                    element={
                        <HomePage
                            navigateToExplorer={navigateToExplorer}
                            navigateToChat={navigateToChat}
                            navigateToConfig={navigateToConfig}
                            navigateToDataManagement={navigateToDataManagement}
                            navigateToPipeline={navigateToPipeline}
                            navigateToPolicyBot={navigateToPolicyBot}
                            navigateToCPSpecialistFormatter={navigateToCPSpecialistFormatter}
                            authUsername={authUsername}
                            theme={theme}
                        />
                    }
                />
                {/* Authenticated users shouldn't access 2FA routes; guarded by early redirect above */}
                <Route
                    path="/explorer"
                    element={
                        <ProtectedRoute required="page:kn-explorer-access" hide={true}>
                            <AppExplorer
                                isAuthenticated={isAuthenticated}
                                directories={directories}
                                userRole={userRole}
                                setUserRole={setUserRole}
                                initialQuery={initialQuery}
                                setInitialQuery={setInitialQuery}
                                maxLoops={maxLoops}
                                analyzeResponse={analyzeResponse}
                                verifyCheckin={verifyCheckin}
                                useRAG={useRAG}
                                handleSaveConfig={handleSaveConfig}
                                handleLogout={handleLogout}
                                isLoading={isLoading}
                                setIsLoading={setIsLoading}
                                error={error}
                                setError={setError}
                            />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/config"
                    element={
                        <SettingsPage
                            handleLogout={handleLogout}
                            theme={theme}
                            setTheme={setTheme}
                        />
                    }
                />
                <Route
                    path="/data"
                    element={
                        <DataManagementPage
                            directories={directories}
                            handleLogout={handleLogout}
                        />
                    }
                />
                <Route
                    path="/chat"
                    element={
                        <ChatWindow
                            ref={chatWindowRef}
                            isAuthenticated={isAuthenticated}
                            handleLogout={handleLogout}
                            authUsername={authUsername}
                            userLLMSettings={userLLMSettings}
                            onSettingsSavedInChatWindow={handleSettingsSavedInChatWindow}
                            onBackendBusyChange={setIsChatBackendBusy}
                        />
                    }
                />
                <Route
                    path="/pipeline"
                    element={
                        <ProtectedRoute required="pipelines:execute" hide={true}>
                            <PipelineRunnerPage
                                authUsername={authUsername}
                                userLLMSettings={userLLMSettings}
                                handleLogout={handleLogout}
                                onBackendBusyChange={setIsChatBackendBusy}
                            />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/policy-bot"
                    element={
                        <ProtectedRoute required="page:policy-bot-access" hide={true}>
                            <PolicyBot
                                isAuthenticated={isAuthenticated}
                                handleLogout={handleLogout}
                                authUsername={authUsername}
                                userLLMSettings={userLLMSettings}
                                onBackendBusyChange={setIsChatBackendBusy}
                            />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/cp-formatter"
                    element={
                        <ProtectedRoute required="page:policy-bot-access" hide={true}>
                            <CPSpecialistFormatterPage
                                isAuthenticated={isAuthenticated}
                                handleLogout={handleLogout}
                                authUsername={authUsername}
                                userLLMSettings={userLLMSettings}
                                onBackendBusyChange={setIsChatBackendBusy}
                            />
                        </ProtectedRoute>
                    }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </div>
    );
}

function App() {
    return (
        <Router>
            <AppWithRouter />
        </Router>
    );
}

const RootApp = () => (
    <PermissionsProvider>
        <App />
    </PermissionsProvider>
);

export default RootApp;